import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Check, X } from "lucide-react";

const FIELDS = [["name", "Vendor / shop name"], ["phone", "Mobile"], ["address", "Address"], ["gstin", "GSTIN"], ["account_name", "Account holder"], ["account_number", "Account number"], ["ifsc", "IFSC"], ["upi_id", "UPI ID"]];
const empty = () => Object.fromEntries(FIELDS.map(([k]) => [k, ""]));

export function VendorForm({ init, onSave, onCancel }) {
    const [f, setF] = useState({ ...empty(), ...Object.fromEntries(FIELDS.map(([k]) => [k, init?.[k] || ""])) });
    return (
        <div data-testid="vendor-form" className="grid grid-cols-2 gap-2 rounded-2xl border border-orange/30 bg-orange/5 p-3 sm:grid-cols-4">
            {FIELDS.map(([k, l]) => <input key={k} data-testid={`vendor-form-${k}`} value={f[k]} onChange={(e) => setF((s) => ({ ...s, [k]: e.target.value }))} placeholder={l} className="rounded-xl border border-navy/15 bg-white px-3 py-2 text-sm outline-none focus:border-orange" />)}
            <div className="col-span-2 flex gap-2 sm:col-span-4">
                <button data-testid="vendor-form-save" onClick={() => onSave(f)} className="flex items-center gap-1 rounded-full bg-navy px-4 py-2 text-xs font-bold text-white"><Check size={14} /> Save</button>
                <button data-testid="vendor-form-cancel" onClick={onCancel} className="flex items-center gap-1 rounded-full border border-navy/15 px-4 py-2 text-xs font-bold text-navy"><X size={14} /> Cancel</button>
            </div>
        </div>
    );
}

export function VendorList({ vendors, onChanged }) {
    const [edit, setEdit] = useState(null);
    const save = async (f, v) => {
        try {
            if (v) await api.patch(`/admin/vendors/${v.id}`, f); else await api.post("/admin/vendors", f);
            toast.success("Vendor saved"); setEdit(null); onChanged();
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    };
    const approve = async (v) => {
        try { await api.patch(`/admin/vendors/${v.id}`, { status: "APPROVED" }); toast.success(`${v.name} approved`); onChanged(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    };
    return (
        <div className="mt-4 space-y-3">
            {edit === "new" ? <VendorForm onSave={(f) => save(f)} onCancel={() => setEdit(null)} /> : (
                <button data-testid="add-vendor-btn" onClick={() => setEdit("new")} className="flex items-center gap-1.5 rounded-full bg-orange px-4 py-2 text-sm font-bold text-white"><Plus size={15} /> Add vendor</button>
            )}
            <div className="overflow-x-auto rounded-2xl border border-navy/10 bg-white homiie-scroll">
                <table className="w-full text-sm" data-testid="vendor-list-table">
                    <thead className="bg-navy/[0.03] text-left text-xs uppercase tracking-wide text-navy/50">
                        <tr>{["Vendor", "Mobile", "Address", "Bank / UPI", "Status", ""].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                        {vendors.map((v) => edit === v.id ? (
                            <tr key={v.id}><td colSpan={6} className="p-3"><VendorForm init={v} onSave={(f) => save(f, v)} onCancel={() => setEdit(null)} /></td></tr>
                        ) : (
                            <tr key={v.id} className="border-t border-navy/5" data-testid={`vendor-row-${v.vendor_code}`}>
                                <td className="px-4 py-3 font-semibold text-navy">{v.name}<div className="text-[11px] font-normal text-navy/40">{v.vendor_code}{v.added_by_role === "professional" ? " · added by Pro" : ""}</div></td>
                                <td className="px-4 py-3 text-navy/70">{v.phone || "—"}</td>
                                <td className="px-4 py-3 text-navy/60">{v.address || "—"}</td>
                                <td className="px-4 py-3 text-xs text-navy/70">{v.has_bank ? <>{v.upi_id && <div>UPI {v.upi_id}</div>}{v.account_number && <div>{v.account_number} · {v.ifsc}</div>}</> : <span className="font-semibold text-amber-700">Missing</span>}</td>
                                <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${v.status === "APPROVED" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{v.status}</span></td>
                                <td className="whitespace-nowrap px-4 py-3">
                                    <button data-testid={`vendor-edit-${v.vendor_code}`} onClick={() => setEdit(v.id)} className="mr-2 inline-flex items-center gap-1 text-xs font-bold text-navy/60"><Pencil size={12} /> Edit</button>
                                    {v.status !== "APPROVED" && <button data-testid={`vendor-approve-${v.vendor_code}`} onClick={() => approve(v)} className="rounded-full bg-emerald-600 px-3 py-1 text-xs font-bold text-white">Approve</button>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {vendors.length === 0 && <div className="py-12 text-center text-sm text-navy/40">No vendors yet.</div>}
            </div>
        </div>
    );
}
